/* ════════════════════════════════════════════════════════════════════════════
   charged-nights.js — THE ONE JavaScript implementation of the charged-nights
   rule. Must agree with snippets/charged-nights.liquid, night for night.

   Load this BEFORE pp-product.js, collection-split.js and pb-cart.js. It
   defines three globals, and those files call them unqualified, so their own
   local copies must be deleted:

       window.computeNights(startDate, endDate, locationData)
       window.isClosedDay(date, locationData)
       window.getFreeLabel(date, locationData)
       window.computeNightsCart(...)   alias, for pb-cart.js

   ── WHY THIS FILE EXISTS ─────────────────────────────────────────────────────
   The rule was implemented four times: once in Liquid and three times here.
   They disagreed, and the customer saw two different night counts on one
   screen — "1 night charged" in the booking bar and "2 charged" in the booking
   card, for the same booking.

   Three real faults were found in the three copies:

   1. THE DAYLIGHT SAVING BUG.  Every copy did this:

          const start = new Date('2026-10-03');   // UTC midnight
          let current = new Date(start);
          while (current < end) {                 // end is also UTC midnight
              ...
              current.setDate(current.getDate() + 1);   // walks LOCAL time
          }

      `new Date('YYYY-MM-DD')` parses as UTC midnight. `setDate()` walks local
      wall-clock time. In Sydney those two cancel out — until the clocks change.
      Australian daylight saving begins 2am on the first Sunday in October, so a
      booking spanning it loses an hour against the UTC end boundary and the loop
      runs one EXTRA time. That extra day is the return day, which is never a
      night.

      Sat 3 Oct - Tue 6 Oct 2026 is 3 nights. The old code counted 4.

      Fixed here by anchoring every night at 12:00 UTC, stepping in exact
      86,400,000 ms, and reading only UTC date parts. No local time is involved
      at any point, so no clock change can shift a night.

   2. A CRASH IN collection-split.js.  Its getFreeLabel read
      `c.weekday.toLowerCase()` with no null guard and no `!c.date` filter. A
      one-off dated closure has `weekday: null`, so it threw a TypeError on
      every free night. Fixed by the `!c.date && c.weekday` guard below.

   3. THREE DIFFERENT LABELS for the same night. pp-product.js returned the
      generic "the day we're shut" for a holiday and threw away its name;
      collection-split.js returned "Christmas Day free"; pb-cart.js returned
      nothing. This file returns the PLAIN fact — "Labour Day", "Sundays" — and
      leaves the phrasing to the caller.

   ── THE CLOSED-DAY POLARITY, WHICH IS COUNTER-INTUITIVE ──────────────────────
   A recurring closure means CLOSED when `enabled` is FALSE.

   Confirmed against live data: at Sydney - St Peters, Monday to Saturday carry
   `enabled: true` and Sunday carries `enabled: false`, and Sunday is the day
   the location is shut. The field name reads like the opposite of what it does.
   Renaming it is queued in FIELD-TRUTH.md. Until then, do not "tidy" this test.

   ── A NIGHT IS FREE FOR EITHER OF TWO REASONS ─────────────────────────────────
   1. A recurring weekday closure — a trading day with NO override date, matching
      weekday, `enabled === false`.
   2. A holiday — an exact date, or month-and-day when `repeats_annually`.

   ── WHAT CHANGED 11-09-2026, AND WHY ─────────────────────────────────────────
   There used to be a third reason: a "one-off dated closure", any closure record
   carrying a date. It is gone, and a dated record must NEVER make a night free.

   The `closure` metaobject is now Trading Day, and its date field is an HOURS
   OVERRIDE — "on 24 Dec we open 8am and close 1pm so staff get the afternoon".
   The location is OPEN that day. Under the old rule the presence of a date alone
   meant shut, so adding a Christmas Eve early-close record would have handed the
   customer a free night on a day we traded, and under-charged the hire.

   Closed dates have exactly one home now: the Holiday metaobject. If a date is
   not a Holiday, we are open. Do not reintroduce a date test here.

   A night belongs to the day it STARTS on: the night of the 14th is the 14th.
   ════════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var DAY_MS = 86400000;
  var WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  /* Parse 'YYYY-MM-DD' to 12:00 UTC on that calendar day.
     Noon rather than midnight so that no timezone on earth can read a
     different calendar date off it. */
  function toNoonUTC(value) {
    if (value instanceof Date) {
      /* Local parts, not UTC parts. A Date built as new Date(2026, 9, 3) is local
         midnight, whose UTC date is the day before. Whatever local calendar day the
         Date represents is the day the caller meant. */
      return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
    }
    var parts = String(value).slice(0, 10).split('-');
    return Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
  }

  function ymd(date) {
    var y = date.getUTCFullYear();
    var m = String(date.getUTCMonth() + 1).padStart(2, '0');
    var d = String(date.getUTCDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  /* ── civil(): WHICH CALENDAR DAY DOES THIS INPUT MEAN? ────────────────────────
     Read this before changing isClosedDay or getFreeLabel.

     Callers hand these functions two different KINDS of date, and the difference is
     invisible until it silently shifts every closure by a day:

       new Date(2026, 8, 7)      LOCAL midnight — what booking-calendar.js builds for
                                 each grid cell. Its UTC timestamp is still Sept 6.
       new Date(noonUtcMs)       Our own 12:00 UTC anchor, from computeNights.

     An earlier version of this file read getUTCDay()/getUTCFullYear() unconditionally.
     Correct for the anchor, wrong for the grid: cell "7" was tested as Sunday the 6th,
     so every Sunday closure appeared on the Monday and Labour Day appeared the day
     after. The calendar looked plausible and was uniformly one day out.

     The rule: a Date means the calendar day you would READ OFF IT LOCALLY, because
     that is what every caller in this theme constructed it to mean. A string means
     itself. Never mix the two readings.

     Prefer passing a 'YYYY-MM-DD' string — it cannot be misread. */
  function civil(input) {
    var y, m, d;

    if (input instanceof Date) {
      y = input.getFullYear();
      m = input.getMonth();
      d = input.getDate();
    } else {
      var p = String(input).slice(0, 10).split('-');
      y = Number(p[0]); m = Number(p[1]) - 1; d = Number(p[2]);
    }

    var mm = String(m + 1).padStart(2, '0');
    var dd = String(d).padStart(2, '0');

    return {
      ymd:  y + '-' + mm + '-' + dd,
      mmdd: mm + '-' + dd,
      /* Weekday via a UTC-anchored probe. new Date(y, m, d).getDay() would do, but
         building the probe in UTC keeps this function free of local-time arithmetic
         entirely, so no clock change can reach it. */
      dow:  new Date(Date.UTC(y, m, d, 12, 0, 0)).getUTCDay()
    };
  }

  function isClosedDay(date, locationData) {
    var closures = (locationData && locationData.closures) || [];
    var holidays = (locationData && locationData.holidays) || [];

    var c = civil(date);
    var dayName = WEEKDAYS[c.dow];
    var yyyy_mm_dd = c.ymd;
    var mm_dd = c.mmdd;

    /* A dated trading-day record is an HOURS OVERRIDE, not a closure — see the header.
       The loop that used to sit here returned true for any record with a matching date,
       which would make an early-closing day free. Closed dates are Holidays, tested
       below. */

    // 1. Recurring weekday closure. `enabled === false` means closed.
    for (var j = 0; j < closures.length; j++) {
      var c = closures[j];
      if (!c.date && c.weekday && c.weekday.toLowerCase() === dayName && c.enabled === false) {
        return true;
      }
    }

    // 3. Holiday
    for (var k = 0; k < holidays.length; k++) {
      var h = holidays[k];
      if (!h.date) continue;
      if (h.repeats_annually ? h.date.slice(5, 10) === mm_dd : h.date.slice(0, 10) === yyyy_mm_dd) {
        return true;
      }
    }

    return false;
  }

  /* The plain reason this night is free — "Labour Day" or "Sunday". The caller adds the
     wording around it. A holiday's name beats a bare weekday: it tells the customer
     more. */
  function getFreeLabel(date, locationData) {
    var closures = (locationData && locationData.closures) || [];
    var holidays = (locationData && locationData.holidays) || [];

    var cv = civil(date);
    var dayName = WEEKDAYS[cv.dow];
    var yyyy_mm_dd = cv.ymd;
    var mm_dd = cv.mmdd;

    /* The dated-closure branch that opened this function is gone, for the reason in the
       header: a dated trading day is an hours override, its night is charged, and so it
       has no free-night label to give. A holiday's own name is the label for a closed
       date — the loop immediately below. `yyyy_mm_dd` is still read by it. */

    for (var k = 0; k < holidays.length; k++) {
      var h = holidays[k];
      if (!h.date) continue;
      if (h.repeats_annually ? h.date.slice(5, 10) === mm_dd : h.date.slice(0, 10) === yyyy_mm_dd) {
        return h.name || "the day we're shut";
      }
    }

    for (var j = 0; j < closures.length; j++) {
      var c = closures[j];
      if (!c.date && c.weekday && c.weekday.toLowerCase() === dayName && c.enabled === false) {
        /* Singular. This used to append 's' — "Sundays" — which describes the recurring
           CLOSURE RULE rather than this booking. Every caller uses the label to explain
           one customer's hire ("you're not charged for Sunday and Labour Day"), where
           the plural reads as though several were involved.

           Safe while the online cap is 7 nights, since a hire can contain at most one
           of any weekday. Even past that, computeNights de-duplicates labels, so two
           Sundays in a longer hire still produce one entry — the plural would be more
           accurate there, but nothing can currently book it. If the cap ever rises
           above 7, revisit this and count occurrences rather than guessing. */
        return c.weekday.charAt(0).toUpperCase() + c.weekday.slice(1).toLowerCase();
      }
    }

    return null;
  }

  function computeNights(startDate, endDate, locationData) {
    if (!startDate || !endDate) {
      return { nightsTotal: 0, nightsCharged: 0, nightsFree: 0, freeLabels: [] };
    }

    var startMs = toNoonUTC(startDate);
    var endMs = toNoonUTC(endDate);
    var nightsTotal = Math.round((endMs - startMs) / DAY_MS);

    if (!(nightsTotal > 0)) {
      return { nightsTotal: 0, nightsCharged: 0, nightsFree: 0, freeLabels: [] };
    }

    var nightsCharged = 0;
    var freeLabels = [];
    var seen = {};

    for (var i = 0; i < nightsTotal; i++) {
      /* Pass the STRING, not the Date. The walk is anchored at 12:00 UTC, so its UTC
         parts are the civil day we mean; handing over a string removes any chance of
         the receiving end reading it the other way. */
      var night = ymd(new Date(startMs + i * DAY_MS));
      if (isClosedDay(night, locationData)) {
        var label = getFreeLabel(night, locationData);
        if (label && !seen[label]) {
          seen[label] = true;
          freeLabels.push(label);
        }
      } else {
        nightsCharged++;
      }
    }

    /* A hire cannot be zero-charged. If every night fell on a closure the
       booking should never have been offerable — the calendar prevents it.
       Clamp rather than give the hire away. Matches charged-nights.liquid. */
    if (nightsCharged < 1) nightsCharged = 1;

    return {
      nightsTotal: nightsTotal,
      nightsCharged: nightsCharged,
      nightsFree: nightsTotal - nightsCharged,
      freeLabels: freeLabels
    };
  }

  /* Exposed because other files walk dates too — the cart's night ledger and its
     event-date dropdown. Every walk in the theme must anchor the same way or they
     drift apart across a daylight-saving change. */
  /* ── The booking's cart attributes, built in ONE place ──────────────────────
     Both the global booking bar and the cart page write these. They were about to be
     built twice, which is how the night count ended up implemented seven times, so the
     builder lives here next to the rule it depends on.

     Human-readable values AND machine values, deliberately:
       'Pick up' / 'Return'          DD-MM-YYYY, per FIELD-TRUTH §5. What ops reads
       '_pick_up_iso' / '_return_iso'  ISO, the only format that parses unambiguously
     SINGLE underscore. A double underscore would make them unreadable from Liquid and
     the Ajax API, and pb-cart.liquid reads them back. */
  function dmy(v) {
    const d = new Date(toNoonUTC(v));
    return String(d.getUTCDate()).padStart(2, '0') + '-' +
           String(d.getUTCMonth() + 1).padStart(2, '0') + '-' + d.getUTCFullYear();
  }

  /* ── THE ONE HOURS PARSER — B19 + #64, 11-09-2026 ──────────────────────────────

     There were FIVE implementations of this before today, and they disagreed:

       charged-nights.js  hoursFor()           getUTCDay, stripped the prefix
       booking-calendar.js  getHours()         getDay (LOCAL), stripped the prefix
       pp-product.js:687    getLocationHours() getDay (LOCAL), stripped the prefix
       pp-product.js:2295   getLocationHours() getDay (LOCAL), DID NOT STRIP  ← #65
       pb-cart.js:947       getHours()         getUTCDay, stripped the prefix

     Two bugs fell out of that. The three getDay() versions read the browser's local
     weekday off a date built from a UTC-noon timestamp, which is the same timezone
     class of bug the dmy() comment below describes — safe in Australia, wrong in the
     Americas. And pp-product.js:2295 never stripped the weekday prefix, so adding to
     cart from a product page stamped "Mon 8am – 3pm" onto the order while the booking
     bar and the cart page stamped "8am – 3pm" for the identical booking. Logged as #65.

     All four call sites now come here. Do not reintroduce a local copy.

     ── SPECIAL TRADING DATES (#64) ──────────────────────────────────────────────

     `pickup_hours` / `return_hours` are a SEVEN-element weekly array, indexed by
     weekday. A Special Trading Date is one date, so it has no slot in that array and
     B17 deliberately left it out of them. This function is where it gets applied,
     because this is the first point at which a specific DATE is known.

     Which end matters: the override is applied when the date IS the pick-up day (for
     the pick-up window) or IS the return day (for the return window). A Special
     Trading Date that merely falls in the middle of a hire changes nothing — nobody
     is at the counter that day. That follows from the call sites, which pass `start`
     for pickup and `end` for return, and needs no test here.

     MATCH ON DATE, IGNORE `weekday`. The live Stocktake record says "Monday" but
     30-09-2026 is a Wednesday. Nothing has ever read the weekday of a dated record,
     which is why it has never mattered — do not start.

     A dated record with `enabled` false is NOT a closure. Closed dates are Holidays,
     and only Holidays. See the header of this file.
  */
  function time12(t) {
    /* Twin of snippets/time-12h.liquid. The two MUST agree: that snippet builds the
       weekly strings server-side, this builds the override client-side, and both feed
       the same display. 08:00 -> 8am, 15:00 -> 3pm, 08:30 -> 8:30am, 00:00 -> 12am. */
    if (!t) return '';
    const parts = String(t).split(':');
    const h = parseInt(parts[0], 10);
    if (isNaN(h)) return '';
    const m = parts[1] || '00';
    const sfx = h >= 12 ? 'pm' : 'am';
    let h12 = h > 12 ? h - 12 : h;
    if (h === 0) h12 = 12;
    return m === '00' ? h12 + sfx : h12 + ':' + m + sfx;
  }

  function datedTradingDay(yyyy_mm_dd, locationData) {
    const recs = (locationData && locationData.closures) || [];
    for (var i = 0; i < recs.length; i++) {
      const d = recs[i].date;
      if (d && String(d).slice(0, 10) === yyyy_mm_dd) return recs[i];
    }
    return null;
  }

  /* which: 'pickup' | 'return'. Returns the window WITHOUT its weekday prefix —
     "8am – 3pm", "Closed", or '' when there is nothing to say. */
  function hoursFor(v, locationData, which) {
    const iso = civil(v).ymd;
    const rec = datedTradingDay(iso, locationData);

    if (rec) {
      const from = which === 'return' ? rec.return_from : rec.pickup_from;
      const to   = which === 'return' ? rec.return_to   : rec.pickup_to;
      const f = time12(from), t = time12(to);
      if (f && t) return f + ' – ' + t;
      /* A dated record with no times for this end tells us nothing about it — fall
         through to the weekday rather than showing a blank or an invented window. */
    }

    const arr = which === 'return'
      ? (locationData && locationData.return_hours)
      : (locationData && locationData.pickup_hours);
    if (!arr || !arr.length) return '';
    const d = new Date(toNoonUTC(v));
    const idx = d.getUTCDay() === 0 ? 6 : d.getUTCDay() - 1;
    return String(arr[idx] || '').replace(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s*/i, '').trim();
  }

  /* ── C21 / #59: THE SAME-DAY CUT-OFF ──────────────────────────────────────────

     Today stops being selectable once the cut-off has passed, per location, per day.
     The cut-off is `close_time` minus `same_day_cut_off` minutes — 16:00 less 60 is
     15:00 on every current record — unless `same_day_cut_off_override` names an exact
     time, in which case that wins outright.

     Counted back from CLOSE, not from `pickup_to`, deliberately. A location open until
     4pm with pick-ups to 3pm can still take a booking at 2:55pm and hand it over in the
     last hour; the cut-off is about whether staff can pick and prep, not about the
     advertised window.

     ── WHY Intl AND NOT new Date() ─────────────────────────────────────────────────

     The obvious implementation reads the browser clock, and the browser clock belongs
     to the CUSTOMER. Someone in Perth is two hours behind Sydney: at 3:30pm Sydney
     their clock says 1:30pm, the cut-off would not have passed for them, and they could
     book a same-day hire the warehouse has already stopped taking. Someone in New
     Zealand would be cut off two hours early. Neither errors, and both look correct to
     whoever is testing in Sydney.

     So the comparison is made in the STORE's timezone via Intl.DateTimeFormat, which
     resolves the offset and the DST state for that zone on that date without any
     arithmetic here. Both locations are in the same zone today; if that ever stops
     being true the zone moves onto the location record and this takes it from there.

     formatToParts rather than parsing a formatted string: the parts are named, so no
     locale can reorder them under us. 'en-CA' only shapes the numerals.

     ── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────────────────

     A day with `enabled: false` returns false — no cut-off. Whether a customer should
     be able to select a CLOSED day as their pick-up day is a live question (they can
     today, and the day shows as a free night), but it is not this one, and answering it
     here would change behaviour nobody asked about. Flagged rather than decided.

     Returns false whenever it cannot tell — no location, no record, no close time. The
     cut-off removes a day a customer would otherwise be offered, so silence has to mean
     "leave it selectable"; the alternative is a warehouse that looks shut because a
     field is blank.
  */
  function minutesOf(hhmm) {
    if (!hhmm) return null;
    var p = String(hhmm).split(':');
    var h = parseInt(p[0], 10), m = parseInt(p[1] || '0', 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  var WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
                       'Thursday', 'Friday', 'Saturday'];

  /* The trading-day record governing one date: a Special Trading Date for it if there
     is one, otherwise that date's weekday. Same precedence as hoursFor(), and matched
     on DATE with the record's own weekday ignored — the live Stocktake record says
     Monday and 30-09-2026 is a Wednesday. */
  function tradingDayFor(ymd, locationData) {
    var recs = (locationData && locationData.closures) || [];
    var i;
    for (i = 0; i < recs.length; i++) {
      if (recs[i].date && String(recs[i].date).slice(0, 10) === ymd) return recs[i];
    }
    var wanted = WEEKDAY_NAMES[civil(ymd).dow];
    for (i = 0; i < recs.length; i++) {
      if (!recs[i].date && String(recs[i].weekday || '').toLowerCase() === wanted.toLowerCase()) {
        return recs[i];
      }
    }
    return null;
  }

  function storeNow(tz) {
    var parts = {};
    try {
      var fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
      fmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      /* An unknown zone throws. Fall back to the browser clock rather than blocking
         nothing at all — wrong by an offset beats not working. */
      var d = new Date();
      return {
        ymd: d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' +
             String(d.getDate()).padStart(2, '0'),
        minutes: d.getHours() * 60 + d.getMinutes(),
        seconds: d.getSeconds(),
        zoneFailed: true
      };
    }
    /* Some engines render midnight as hour 24. */
    var h = parseInt(parts.hour, 10) % 24;
    return {
      ymd: parts.year + '-' + parts.month + '-' + parts.day,
      minutes: h * 60 + parseInt(parts.minute, 10),
      /* Seconds, added with #84 for the countdown. parts.second is absent on an engine
         that ignores the option, so it falls to 0 rather than NaN. */
      seconds: parseInt(parts.second, 10) || 0
    };
  }

  /* The cut-off time for a given date as a minutes-from-midnight number, or null when
     there isn't one. Exposed so a surface can SAY the time rather than only obey it. */
  function cutOffMinutes(ymd, locationData) {
    var rec = tradingDayFor(ymd, locationData);
    if (!rec || rec.enabled === false) return null;
    var override = minutesOf(rec.same_day_cut_off_override);
    if (override !== null) return override;
    var close = minutesOf(rec.close_time);
    var offset = parseInt(rec.same_day_cut_off, 10);
    if (close === null || isNaN(offset)) return null;
    return close - offset;
  }

  function sameDayCutOffPassed(locationData, tz) {
    if (!locationData) return false;
    var now = storeNow(tz || 'Australia/Sydney');
    var cut = cutOffMinutes(now.ymd, locationData);
    if (cut === null) return false;
    return now.minutes >= cut;
  }

  /* Formats today's cut-off as "3pm" for display. Returns '' when there isn't one. */
  function cutOffLabel(locationData, tz) {
    if (!locationData) return '';
    var now = storeNow(tz || 'Australia/Sydney');
    var cut = cutOffMinutes(now.ymd, locationData);
    if (cut === null) return '';
    var h = Math.floor(cut / 60), m = cut % 60;
    return time12(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
  }

  /* ── #84 / #85: THE ONE BOOKING-DATE VALIDATOR ────────────────────────────────

     A date is chosen once and then trusted forever. Three things can make it wrong
     afterwards without anybody touching it:

       past      the day has gone. A cart left open overnight has a pick-up date in the
                 past, and updateCheckoutValidation() only ever asked whether a date
                 EXISTED. This one predates the cut-off work and is live today.
       cutoff    today was fine at 2pm and is not at 3:01pm.
       location  legal at Sydney, not at Melbourne — a different cut-off, a holiday at
                 one and not the other, a Special Trading Date.

     All three are the same question: is this booking still bookable? So there is ONE
     function and every surface asks it, rather than each deciding for itself. That is
     the lesson of the five hours parsers (B19), applied before the duplication happens.

     Returns { ok, reason, cutOffAt } — reason is 'past' | 'cutoff' | 'closed' | null,
     and cutOffAt is that location's cut-off today as a label, so a caller can SAY the
     time rather than only obey it.

     WHAT IT DOES NOT DECIDE: what to do about a failure. The booking bar clears the
     dates, because the customer is looking at the calendar and re-picking is one tap.
     The cart keeps them and stops checkout, because a customer arriving cold would
     otherwise lose an earlier choice without understanding why. Same verdict, different
     remedies, chosen by the surface that has the context.

     Judged in the STORE's timezone, like the cut-off itself — see storeNow().
  */
  /*  ── #139: THIS VALIDATED ONE DATE AND ITS NAME SAID TWO ────────────────────
      Reported 17-09-2026: dates 24-25 September against Sydney, branch switched to
      Melbourne where 25 September is closed for the AFL Grand Final, booking stood.

      booking-bar.js already re-validates on a branch switch — applyLocationSelection
      calls runCutOffGuard(true), and its comment states confidently that "one
      validator covers all of them ... a holiday at one location and not the other, a
      Special Trading Date, a closed weekday". That comment trusted this function's
      NAME. Three limits underneath it, and Ben's case cleared all three:

        1. Only startIso was ever passed. The return day was never looked at, and
           25 September was the return day.
        2. The closure test sat inside "if (start === now.ymd)", so it only ever
           asked about TODAY. A closed day next week was never tested even as a
           pick-up.
        3. The function is a CUT-OFF validator. Closure appears in it only because a
           day that is not trading cannot have a cut-off — it was never a closure
           check, it just looked like one from the outside.

      So the fix is not a new caller. It is this function doing what it is named for.

      ── WHY ONLY THE ENDPOINTS ──────────────────────────────────────────────────
      A closed day INSIDE the range is legitimate and must stay legitimate — it is
      a free night, which is the whole charged-nights mechanism, and computeNights
      above already treats it that way. What cannot happen is collecting or returning
      on a day the branch is shut. So the pick-up day and the return day are tested,
      and the days between are not.

      ── SIGNATURE ───────────────────────────────────────────────────────────────
      endIso is appended, not inserted, so the one existing three-argument caller
      keeps working unchanged and the return value keeps its shape. A caller that
      does not pass it gets exactly the old behaviour plus the future-closure test on
      the pick-up day. */
  function validateBookingDates(startIso, locationData, tz, endIso) {
    var verdict = { ok: true, reason: null, cutOffAt: '', daysAgo: 0, closedOn: '' };
    if (!startIso || !locationData) return verdict;

    var now = storeNow(tz || 'Australia/Sydney');
    var start = civil(startIso).ymd;

    if (start < now.ymd) {
      verdict.ok = false;
      verdict.reason = 'past';
      /*  How stale, in whole days, so the surface can say "yesterday" rather than
          repeating a date the customer can already see is wrong.

          Counted in UTC-noon milliseconds on both sides, which is the same trick
          toNoonUTC() exists for: a plain day subtraction across a DST boundary is off
          by an hour, and an hour is enough to turn 1 day into 0. Noon is far from any
          transition, so the division is always whole. */
      var MS_DAY = 86400000;
      verdict.daysAgo = Math.round((toNoonUTC(now.ymd) - toNoonUTC(start)) / MS_DAY);
      return verdict;
    }

    /*  ── ENDPOINT CLOSURES, ON ANY DAY ─────────────────────────────────────────
        Tested before the cut-off, and outside the "is it today" branch below, because
        a branch being shut on a future day is just as disqualifying as being shut
        this morning. isClosedDay covers weekly closures, one-off trading days and
        holidays in one call, which is why this is not three tests.

        closedOn names WHICH end failed, so the surface can say "you cannot return on
        the 25th" rather than "these dates do not work" and leave the customer to
        work out which one to change. */
    if (isClosedDay(start, locationData)) {
      verdict.ok = false;
      verdict.reason = 'closed';
      verdict.closedOn = 'start';
      return verdict;
    }
    if (endIso && isClosedDay(civil(endIso).ymd, locationData)) {
      verdict.ok = false;
      verdict.reason = 'closed';
      verdict.closedOn = 'end';
      return verdict;
    }

    /* Only today can be cut off — tomorrow has its own cut-off tomorrow. */
    if (start === now.ymd) {
      var cut = cutOffMinutes(start, locationData);
      if (cut !== null && now.minutes >= cut) {
        verdict.ok = false;
        verdict.reason = 'cutoff';
        verdict.cutOffAt = cutOffLabel(locationData, tz);
      }
    }
    return verdict;
  }

  /* Seconds until today's cut-off. Negative once it has passed, null when there is
     none. The banner countdown reads this every tick rather than decrementing a number
     of its own, so a laptop that slept through the cut-off cannot drift past it. */
  function secondsToCutOff(locationData, tz) {
    if (!locationData) return null;
    var now = storeNow(tz || 'Australia/Sydney');
    var cut = cutOffMinutes(now.ymd, locationData);
    if (cut === null) return null;
    return (cut * 60) - (now.minutes * 60) - now.seconds;
  }

  /*  ── BLACKOUT TIMES IN THE BOOKING — dev-build 201 ──────────────────────────
      A timed edge day (item_blackout return_by_time / pickup_from_time) narrows the
      branch's window: a late pick-up keeps the close and moves the open to the
      blackout time; an early return keeps the open and moves the close. Read through
      window.bbEdgeTime (booking-calendar.js), the one owner of "which time applies",
      with the booking's own branch. No calendar on the page, no change.           */
  function edgeWindow(iso, locationData, which, windowText) {
    if (typeof window.bbEdgeTime !== 'function' || !locationData) return windowText;
    var gid = locationData.gid ? (String(locationData.gid).indexOf('gid://') === 0
      ? String(locationData.gid) : 'gid://shopify/Metaobject/' + locationData.gid) : null;
    var t = window.bbEdgeTime(String(iso).slice(0, 10), which, gid);
    if (!t) return windowText;
    var parts = String(windowText || '').split(' – ');
    if (parts.length !== 2) return t;
    return which === 'pickup' ? t + ' – ' + parts[1] : parts[0] + ' – ' + t;
  }

  function buildBookingAttributes(start, end, locationData) {
    const { nightsCharged } = computeNights(start, end, locationData);
    return {
      'Location':        locationData.name || '',
      'Location suburb': locationData.suburb || '',
      'Charged nights':  String(nightsCharged),
      'Pick up':         dmy(start),
      'Pick up time':    edgeWindow(start, locationData, 'pickup', hoursFor(start, locationData, 'pickup')),
      'Return':          dmy(end),
      'Return time':     edgeWindow(end, locationData, 'return', hoursFor(end, locationData, 'return')),
      '_pick_up_iso':    String(start).slice(0, 10),
      '_return_iso':     String(end).slice(0, 10)
    };
  }

  /* ── THE ONE CART-ATTRIBUTE WRITER ──────────────────────────────────────────

     Five files were POSTing to /cart/update.js with their own hand-built attribute
     objects: this one, booking-bar.js, pb-cart.js, pp-product.js and — briefly, and
     wrongly — a tenth asset written for step 2 of the cart. That is the shape of #67,
     "the PDP hand-builds cart attributes instead of calling the builder", repeated
     once per surface.

     So the write lives here, beside the builder, and takes a PARTIAL set. Callers say
     which attributes they own and nothing else; Shopify merges the rest.

     Serialised through one promise chain, which is the part a per-surface copy cannot
     get right. Every one of those five did read-then-diff-then-write, and two of them
     running at once both read the pre-write cart, so the second POST overwrote the
     first with a diff computed against stale data. Sending only the keys you own
     narrows that window; queueing closes it. Same reason pb-cart.js keeps cart WRITES
     sequential while parallelising product reads.

     Returns whether it actually wrote, because pb-cart.js re-prices on
     bookingbar:changed and reloads if the bar wrote — an unconditional true would put
     the cart page back into the reload loop that was removed on 10-09. */
  var attrChain = Promise.resolve();

  async function writeCartAttributes(partial) {
    if (!partial) return false;
    var keys = Object.keys(partial);
    if (!keys.length) return false;

    var run = attrChain.then(async function () {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); }).catch(function () { return null; });
      if (!cart) return false;
      var existing = cart.attributes || {};
      var differs = keys.some(function (k) {
        return String(existing[k] ?? '') !== String(partial[k] ?? '');
      });
      if (!differs) return false;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: partial })
      }).catch(function () { return null; });
      return true;
    });

    /*  The chain must not break on a failed write, or every later write is skipped.
        The caller still sees the rejection through `run`. */
    attrChain = run.catch(function () { return false; });
    return run;
  }

  /* Writes only what differs, and reports whether it wrote anything.

     Idempotence is not a nicety here. pb-cart.js re-prices the cart on
     bookingbar:changed and reloads if it wrote — an unconditional write from the bar
     would put the cart page into the reload loop we just removed. Returns false when
     nothing changed so no caller has a reason to reload. */
  async function writeBookingAttributes(start, end, locationData) {
    if (!start || !end || !locationData) return false;
    /*  The read, the diff and the POST moved into writeCartAttributes so the booking
        bar and the cart's step 2 go through one queue. Behaviour and return value are
        unchanged — this is now the booking-shaped call in front of the general one. */
    return writeCartAttributes(buildBookingAttributes(start, end, locationData));
  }

  window.civilDate = civil;
  window.toNoonUTC = toNoonUTC;
  /* The one hours parser. booking-calendar.js, pp-product.js (twice) and pb-cart.js
     all read this — see the block comment above hoursFor(). */
  window.hoursFor = hoursFor;
  /* C21. booking-calendar.js reads these; see the block comment above them. */
  window.sameDayCutOffPassed = sameDayCutOffPassed;
  window.cutOffLabel = cutOffLabel;
  window.tradingDayFor = tradingDayFor;
  /* #84 / #85 — the one validator, and the clock the banner reads. */
  window.validateBookingDates = validateBookingDates;
  window.secondsToCutOff = secondsToCutOff;
  window.buildBookingAttributes = buildBookingAttributes;
  window.writeBookingAttributes = writeBookingAttributes;
  /* The general one. Step 2 of the cart uses it; pp-product.js and booking-bar.js
     should converge on it when #67 is taken. */
  window.writeCartAttributes = writeCartAttributes;
  window.computeNights = computeNights;
  window.isClosedDay = isClosedDay;
  window.getFreeLabel = getFreeLabel;
  window.computeNightsCart = computeNights;   // pb-cart.js call signature is identical

  /* ── ONE SHORT-DATE FORMAT, AND WHY IT IS FIXED RATHER THAN LOCALISED ──────────
     "Sep" vs "Sept" has now surfaced twice. The cause is not a typo, it is two
     different sources of month names in one page:

       · toLocaleDateString('en-AU', {month:'short'}) — what booking-calendar.js and
         booking-bar.js used. en-AU's Intl data abbreviates September to "Sept", four
         letters, and only September.
       · a hard-coded ['Jan'…'Sep'…] array — what pb-cart.js (five copies),
         pb-cart-drawer.js and pp-product.js used.
       · Liquid's '%b', which is Ruby strftime and gives "Sep".

     Every other month agrees, so nothing catches it until a September booking. Then
     the bar says "Fri 11 Sept" and the cart says "Fri 11 Sep", and a server-rendered
     line flickers by one character when the JS rewrites it.

     So: one fixed list, here, used by every surface and mirrored by
     snippets/date-short.liquid. "Sept" is the form already on screen and signed off,
     so that is the one kept.

     FIXED, NOT LOCALISED, on purpose. Intl data varies by browser and OS version — the
     fragility logged when the booking bar was patched. A store that sells in one
     country does not need the month name negotiated at runtime.

     Anchored at midday UTC like every other date walk in this file: a date-only string
     parsed as UTC midnight and read in local time is the previous day in Sydney. */
  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function formatBookingDate(value) {
    if (!value) return '';
    var d = new Date(toNoonUTC(typeof value === 'string' ? value : value.toISOString().slice(0, 10)));
    if (isNaN(d)) return '';
    return DAYS_SHORT[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS_SHORT[d.getUTCMonth()];
  }

  window.MONTHS_SHORT = MONTHS_SHORT;
  window.DAYS_SHORT = DAYS_SHORT;
  window.formatBookingDate = formatBookingDate;
})();

/*  575 — ONE wording for hire length, everywhere it's shown. The ACTUAL nights with the
    gear, never the charged number (a customer who picked 2 nights must never read "1").
      short: "2 nights · 1 free"
      long : "2 nights · 1 free (we're closed on Sunday)" / "(we're closed for Christmas Day)"
             more than one closed night: "5 nights · 2 free (we're closed)"
    Prices still use the charged number; this is wording only. */
window.djwNightsText = function (start, end, loc, mode) {
  if (typeof window.computeNights !== 'function') return '';
  var n = window.computeNights(start, end, loc);
  var T = n.nightsTotal || 0, F = n.nightsFree || 0;
  if (!T) return '';
  var s = T + ' ' + (T === 1 ? 'night' : 'nights');
  if (!F) return s;
  s += ' \u00B7 ' + F + ' free';
  /* 605 (Ben 22-10): the reason moved into the "?" tooltip (djwNightsWhy), so the long form
     is the same as the short one. Plain-text surfaces simply show "2 nights · 1 free". */
  return s;
};

/*  605: the reason for the free nights, naming every closed day in the hire.
    "We’re closed for Christmas Day, Boxing Day and Sunday, so you don’t pay for those nights." */
window.djwNightsWhy = function (start, end, loc) {
  if (typeof window.computeNights !== 'function') return '';
  var n = window.computeNights(start, end, loc), F = n.nightsFree || 0;
  if (!F) return '';
  var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var L = [], seen = {};
  (n.freeLabels || []).forEach(function (x) { if (x && !seen[x]) { seen[x] = 1; L.push(x); } });
  var list = L.length ? (L.length === 1 ? L[0] : L.slice(0, -1).join(', ') + ' and ' + L[L.length - 1]) : '';
  var lead = !list ? 'We\u2019re closed' : (L.length === 1 && days.indexOf(L[0]) >= 0 ? 'We\u2019re closed on ' : 'We\u2019re closed for ') + list;
  return lead + (F === 1 ? ', so you don\u2019t pay for that night.' : ', so you don\u2019t pay for those nights.');
};

/*  577: the same long wording as HTML, with the "(we're closed …)" part in its own span
    (.djw-nights-why) so each surface can decide whether it drops onto a second line. */
window.djwNightsHTML = function (start, end, loc) {
  var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
  var t = window.djwNightsText(start, end, loc, 'short'), why = window.djwNightsWhy(start, end, loc);
  return esc(t) + (why ? '<button type="button" class="djw-q" data-djw-tip="' + esc(why) + '" aria-label="' + esc(why) + '">?</button>' : '');
};

/*  605: one tooltip for every "?" on the site. Hover on desktop, tap on touch; tap elsewhere
    or scroll to close. Styles injected here so the cart, drawer, card and calendar all match. */
(function () {
  if (window.__djwQ) return; window.__djwQ = 1;
  var st = document.createElement('style');
  st.textContent = '.djw-q{all:unset;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;margin-left:6px;border-radius:50%;background:#e4e4ea;color:#55555f;font-family:inherit;font-size:9px;font-weight:700;line-height:1;cursor:help;flex:none;vertical-align:middle;transition:background .12s,color .12s}' +
    '.djw-q:hover,.djw-q[aria-expanded=true]{background:#021ab3;color:#fff}.djw-q:focus-visible{outline:2px solid #021ab3;outline-offset:2px}' +
    /* 614: the "?" disappears into the skeleton with the nights beside it — drawer, cart page,
       and anything else that greys its text while loading. */
    '.pb-cart-drawer.is-pending .djw-q,.pb-cart-drawer.is-loading .djw-q,.pb-cart-drawer.is-repricing .djw-q,' +
    'html.djw-cart-busy .pb-cart-drawer.is-active .djw-q,html.djw-remote-busy .pb-cart-drawer.is-active .djw-q,' +
    'html body:has(.bkr.is-updating,.bkr.is-removing) .is-d5 .d5-total-n .djw-q,' +
    '.is-d5.is-d5-loading .d5-total-n .djw-q,.is-d5.is-updating .d5-total-n .djw-q,' +
    '.pp-bc [data-checking] .djw-q,.is-updating .djw-q{visibility:hidden!important}' +
    '.djw-q-tip{position:fixed;z-index:2147483000;max-width:240px;background:#101014;color:#fff;font-family:inherit;font-size:12.5px;font-weight:400;line-height:1.4;letter-spacing:0;padding:8px 11px;border-radius:9px;box-shadow:0 6px 18px rgba(16,16,20,.18);pointer-events:none;opacity:0;transform:translateY(3px);transition:opacity .12s,transform .12s}' +
    '.djw-q-tip.is-on{opacity:1;transform:none}';
  document.head.appendChild(st);
  var tip = null, cur = null;
  function show(b) {
    if (!tip) { tip = document.createElement('div'); tip.className = 'djw-q-tip'; tip.setAttribute('role', 'tooltip'); document.body.appendChild(tip); }
    cur = b; b.setAttribute('aria-expanded', 'true');
    tip.textContent = b.getAttribute('data-djw-tip') || '';
    var r = b.getBoundingClientRect(); tip.style.left = '0px'; tip.style.top = '0px'; tip.classList.add('is-on');
    var w = tip.offsetWidth, hgt = tip.offsetHeight;
    var x = Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2));
    /* 631: sat low — clear the "?" by 10px and ignore the 3px slide-in offset. */
    var y = r.top - hgt - 10; if (y < 8) y = r.bottom + 10;
    tip.style.left = x + 'px'; tip.style.top = y + 'px';
  }
  function hide() { if (cur) cur.setAttribute('aria-expanded', 'false'); cur = null; if (tip) tip.classList.remove('is-on'); }
  var touch = window.matchMedia && window.matchMedia('(hover: none)').matches;
  document.addEventListener('mouseover', function (e) { if (touch) return; var b = e.target.closest && e.target.closest('.djw-q'); if (b) show(b); });
  document.addEventListener('mouseout', function (e) { if (touch) return; var b = e.target.closest && e.target.closest('.djw-q'); if (b && !b.contains(e.relatedTarget)) hide(); });
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.djw-q');
    if (b) { e.preventDefault(); e.stopPropagation(); if (cur === b) hide(); else show(b); return; }
    hide();
  }, true);
  window.addEventListener('scroll', hide, { passive: true, capture: true });
})();

/*  607: "?" buttons printed by Liquid (cart page Hire total, cart drawer Hire total) get the
    exact reason — every closed day in the hire — from the booking. Runs on load, after any
    cart change, and whenever the drawer re-renders. */
(function () {
  var busy = false;
  function fill() {
    var qs = document.querySelectorAll('.djw-q[data-djw-q]'); if (!qs.length || busy || typeof window.djwNightsWhy !== 'function') return;
    busy = true;
    fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (c) {
      var a = c.attributes || {}, all = window.locationMetaobjects || {};
      var loc = Object.keys(all).map(function (k) { return all[k]; }).filter(function (l) { return l && l.name === a.Location; })[0];
      var why = a._pick_up_iso && a._return_iso ? window.djwNightsWhy(a._pick_up_iso, a._return_iso, loc) : '';
      if (why) document.querySelectorAll('.djw-q[data-djw-q]').forEach(function (b) { b.setAttribute('data-djw-tip', why); b.setAttribute('aria-label', why); });
    }).catch(function () {}).then(function () { busy = false; });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fill); else fill();
  window.addEventListener('load', fill);
  ['djw:cart', 'pbcart:updated', 'cart:reconciled'].forEach(function (e) { document.addEventListener(e, function () { setTimeout(fill, 50); }); });
  new MutationObserver(function (m) { for (var i = 0; i < m.length; i++) if (m[i].addedNodes.length) { var n = m[i].target; if (n.querySelector && n.querySelector('.djw-q[data-djw-q]')) { fill(); break; } } })
    .observe(document.documentElement, { childList: true, subtree: true });
})();
